import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Typography,
  Box,
  Paper,
  Alert,
  CircularProgress,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import { Download, Edit, Send, Refresh, Chat, Close } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { DocumentManager } from '../utils/DocumentManager';
import { styled } from '@mui/material/styles';
import { api } from '../services/api';
import { getErrorMessage } from '../utils/errorUtils';

interface ContractPreviewProps {
  open: boolean;
  onClose: () => void;
  contractData: {
    text_content: string;
    filename: string;
    download_url: string;
    generated_at: string;
    contract_type?: string;
    form_data?: any;
  } | null;
  onDownload: (modifiedContent?: string) => void;
  onDownloadDocx?: (modifiedContent?: string) => void;
  onEdit: () => void;
  downloading?: boolean;
  /** Which tab to show when opening: 'preview' | 'chat' | tab index */
  initialTab?: 'preview' | 'chat' | number;
}

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 48,
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '3px 3px 0 0',
    backgroundColor: theme.palette.primary.main,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  minHeight: 48,
  minWidth: 120,
  padding: theme.spacing(1.5, 3),
  borderTopLeftRadius: 10,
  borderTopRightRadius: 10,
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
  '&.Mui-selected': {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    boxShadow: theme.shadows[2],
  },
}));

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contract-tabpanel-${index}`}
      aria-labelledby={`contract-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
};


const ContractPreview: React.FC<ContractPreviewProps> = ({
  open,
  onClose,
  contractData,
  onDownload,
  onEdit,
  onDownloadDocx,
  downloading = false,
  initialTab = 'preview',
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [modifiedContent, setModifiedContent] = useState(contractData?.text_content || '');
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (contractData?.text_content) {
      setModifiedContent(contractData.text_content);
    }
  }, [contractData]);

  const resolvedInitialTab = useMemo(() => {
    if (typeof initialTab === 'number') {
      return initialTab;
    }
    return initialTab === 'chat' ? 1 : 0;
  }, [initialTab]);

  // Ensure desired initial tab is active when dialog opens or data changes
  React.useEffect(() => {
    if (open) {
      setTabValue(resolvedInitialTab);
    }
  }, [open, resolvedInitialTab, contractData?.filename, contractData?.generated_at]);

  if (!contractData) {
    return null;
  }


  const handleSendMessage = async () => {
    if (!currentMessage.trim() || isProcessing) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: currentMessage.trim(),
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setIsProcessing(true);
    setError('');

    try {
      const response = await api.post('/documents/modify-contract', {
        current_content: modifiedContent,
        modification_request: userMessage.content,
        contract_type: contractData.contract_type || 'rental',
        form_data: contractData.form_data
      });

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: `Upravil jsem smlouvu podle vašeho požadavku: "${userMessage.content}". Prohlédněte si změny v náhledu.`,
        timestamp: new Date()
      };

      setChatMessages(prev => [...prev, assistantMessage]);
      setModifiedContent(response.data.modified_content);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodařilo se upravit smlouvu'));
      // Remove user message if there was an error
      setChatMessages(prev => prev.filter(msg => msg.id !== userMessage.id));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleReset = () => {
    setModifiedContent(contractData.text_content);
    setChatMessages([]);
    setError('');
  };

  const displayContent = modifiedContent || contractData.text_content;

  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    // Ignore backdrop clicks to prevent accidental closing
    if (reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ position: 'relative', pr: 6 }}>
        <Typography variant="h5" component="div">
          Náhled a úprava smlouvy
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Zkontrolujte obsah, upravte podle potřeby, a stáhněte finální PDF
        </Typography>
        <IconButton
          aria-label="Zavřít náhled"
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[600],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Vygenerováno:</strong> {new Date(contractData.generated_at).toLocaleString('cs-CZ')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Můžete smlouvu upravit pomocí AI asistenta nebo stáhnout aktuální verzi.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <StyledTabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Přepínač mezi náhledem smlouvy a AI úpravami"
          >
            <StyledTab
              label="Náhled smlouvy"
              disableRipple
              id="contract-tab-0"
              aria-controls="contract-tabpanel-0"
            />
            <StyledTab 
              label="AI Úpravy" 
              icon={<Chat />}
              iconPosition="start"
              disableRipple
              id="contract-tab-1"
              aria-controls="contract-tabpanel-1"
            />
          </StyledTabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Obsah smlouvy</Typography>
            {chatMessages.length > 0 && (
              <Button
                onClick={handleReset}
                startIcon={<Refresh />}
                size="small"
                variant="outlined"
              >
                Obnovit původní
              </Button>
            )}
          </Box>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 3, 
              bgcolor: 'grey.50', 
              maxHeight: '60vh', 
              overflow: 'auto',
              fontFamily: 'monospace',
              whiteSpace: 'pre-line',
              lineHeight: 1.6
            }}
          >
            <Typography 
              variant="body2" 
              component="div"
              sx={{ 
                fontSize: '0.875rem',
                color: 'text.primary'
              }}
            >
              {displayContent}
            </Typography>
          </Paper>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ height: '60vh', display: 'flex', flexDirection: 'column' }}>
            {/* Chat History */}
            <Box sx={{ 
              flex: 1, 
              overflowY: 'auto', 
              mb: 2, 
              p: 2, 
              border: '1px solid', 
              borderColor: 'divider', 
              borderRadius: 2,
              bgcolor: 'background.paper'
            }}>
              {chatMessages.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Začněte konverzaci s AI asistentem pro úpravu smlouvy
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Například: "Přidej klauzuli o domácích zvířatech" nebo "Změň výpovědní lhůtu na 3 měsíce"
                  </Typography>
                </Box>
              ) : (
                chatMessages.map((message) => (
                  <Box key={message.id} sx={{ mb: 2 }}>
                    {message.type === 'user' ? (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Paper sx={{ 
                          p: 2, 
                          maxWidth: '80%', 
                          bgcolor: '#e3f2fd',
                          borderRadius: 2,
                          borderTopRightRadius: 0
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                            Váš požadavek:
                          </Typography>
                          <Typography variant="body1">
                            {message.content}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {message.timestamp.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Paper>
                      </Box>
                    ) : (
                      <Paper sx={{ 
                        p: 2, 
                        maxWidth: '80%', 
                        bgcolor: '#f0f7ff',
                        border: '1px solid #e3f2fd',
                        borderRadius: 2,
                        borderTopLeftRadius: 0
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: 'primary.main' }}>
                          AI Asistent:
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          {message.content}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {message.timestamp.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                ))
              )}
              
              {isProcessing && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    AI asistent zpracovává vaši úpravu...
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Message Input */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                placeholder="Popište, jak chcete smlouvu upravit (např. 'Přidej podmínku o zákazu kouření')"
                variant="outlined"
                disabled={isProcessing}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <Tooltip
                title="Popište vše svými slovy, do smlouvy půjde vše odborně pomocí AI."
                arrow
              >
                <span>
                  <Button
                    onClick={handleSendMessage}
                    disabled={!currentMessage.trim() || isProcessing}
                    variant="outlined"
                    size="medium"
                    startIcon={isProcessing ? <CircularProgress size={20} /> : <Send />}
                    sx={{
                      alignSelf: 'flex-end',
                      textTransform: 'none',
                      color: '#1976d2',
                      borderColor: '#1976d2',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        borderColor: '#1976d2'
                      }
                    }}
                  >
                    Přidat do smlouvy
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </TabPanel>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
        <Box>
          <Button 
            onClick={onEdit} 
            startIcon={<Edit />}
            variant="outlined"
            size="medium"
            disabled={downloading}
            sx={{ 
              color: '#424242',
              borderColor: '#424242',
              '&:hover': {
                backgroundColor: 'rgba(66, 66, 66, 0.08)',
                borderColor: '#424242'
              }
            }}
          >
            Upravit formulář
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            onClick={onClose} 
            size="medium"
            disabled={downloading}
            sx={{ 
              color: '#424242',
              '&:hover': {
                backgroundColor: 'rgba(66, 66, 66, 0.08)'
              }
            }}
          >
            Zavřít
          </Button>
          {/* Removed bottom toggle (Náhled smlouvy); rely on top tabs */}
          <Button
            onClick={() => onDownload(modifiedContent !== contractData.text_content ? modifiedContent : undefined)}
            variant="outlined"
            size="medium"
            startIcon={downloading ? <CircularProgress size={20} /> : <Download />}
            disabled={downloading}
            sx={{ 
              color: '#1976d2',
              borderColor: '#1976d2',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                borderColor: '#1976d2'
              }
            }}
          >
            {downloading ? 'Stahuji...' : 'Stáhnout PDF'}
          </Button>
          <Button
            onClick={() => {
              const maybeModified = modifiedContent !== contractData.text_content ? modifiedContent : undefined;
              if (onDownloadDocx) {
                onDownloadDocx(maybeModified);
              } else if (contractData) {
                // fallback bez props – spustit stahování přímo
                DocumentManager.downloadDocumentDocx(
                  contractData,
                  () => undefined,
                  () => undefined,
                  maybeModified
                );
              }
            }}
            variant="outlined"
            size="medium"
            startIcon={<Download />}
            disabled={downloading}
            sx={{ 
              color: '#1976d2',
              borderColor: '#1976d2',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                borderColor: '#1976d2'
              }
            }}
          >
            Stáhnout DOCX
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ContractPreview;
